import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertCircle, MessageSquare, User, FileText } from 'lucide-react';
import { toast } from 'react-toastify';
import Backdrop from 'components/Backdrop';
import { verifyGeneralQuestionnaireLink } from 'api/apiHelper';

interface QuestionnaireInfo {
  valid: boolean;
  applicant_name: string;
  job_title: string;
  status: string;
  total_questions: number;
}

interface GeneralQuestionnaireWelcomeScreenProps {
  testToken?: string;
  onTestStarted?: () => void;
}

const GeneralQuestionnaireWelcomeScreen: React.FC<GeneralQuestionnaireWelcomeScreenProps> = ({ testToken: propTestToken, onTestStarted }) => {
  const { testToken: paramTestToken } = useParams<{ testToken: string }>();
  const testToken = propTestToken || paramTestToken;
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [questionnaireInfo, setQuestionnaireInfo] = useState<QuestionnaireInfo | null>(null);

  useEffect(() => {
    if (testToken) {
      verifyQuestionnaire();
    }
  }, [testToken]);

  const verifyQuestionnaire = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await verifyGeneralQuestionnaireLink(testToken!);
      console.log('Questionnaire verification:', response.data);
      
      if (response.data.valid) {
        setQuestionnaireInfo(response.data);
      } else {
        setError(response.data.message || 'Invalid questionnaire link');
        toast.error(response.data.message || 'Invalid questionnaire link');
      }
    } catch (error: any) {
      console.error('Questionnaire verification error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to verify questionnaire link';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleStartQuestionnaire = async () => {
    // In unified flow, start the questionnaire and trigger progression check
    if (window.location.pathname.includes('/unified-test/')) {
      try {
        const { startGeneralQuestionnaire } = await import('api/apiHelper');
        await startGeneralQuestionnaire(testToken!);
        toast.success('Questionnaire started! Loading...');
        // Trigger progression check in UnifiedTestFlow
        if (onTestStarted) {
          setTimeout(() => {
            onTestStarted();
          }, 500);
        }
      } catch (error: any) {
        toast.error('Failed to start questionnaire. Please try again.');
      }
    } else {
      navigate(`/general-questionnaire/${testToken}`);
    }
  };

  if (loading) {
    return <Backdrop message="Verifying questionnaire link..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
          <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-2">Invalid Questionnaire Link</h2>
          <p className="text-center text-gray-600 mb-6">{error}</p>
          <p className="text-sm text-center text-gray-500">
            Please check your email for the correct link or contact support.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-2xl w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-indigo-100 rounded-full mb-4">
            <MessageSquare className="w-10 h-10 text-brand-500" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            General Questionnaire
          </h1>
          <p className="text-gray-600">Share your thoughts and experiences</p>
        </div>

        <div className="bg-indigo-50 rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center">
              <User className="w-5 h-5 text-brand-500 mr-3" />
              <div>
                <p className="text-sm text-gray-600">Applicant</p>
                <p className="font-semibold text-gray-800">{questionnaireInfo?.applicant_name}</p>
              </div>
            </div>
            <div className="flex items-center">
              <FileText className="w-5 h-5 text-brand-500 mr-3" />
              <div>
                <p className="text-sm text-gray-600">Position</p>
                <p className="font-semibold text-gray-800">{questionnaireInfo?.job_title}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-6 mb-6">
          <h3 className="font-semibold text-gray-800 mb-4">Questionnaire Instructions:</h3>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start">
              <span className="inline-block w-6 h-6 bg-brand-500 text-white rounded-full flex-shrink-0 mr-3 text-center text-sm leading-6 font-semibold">1</span>
              <span>This questionnaire contains <strong>{questionnaireInfo?.total_questions || 0} text-based questions</strong></span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-6 h-6 bg-brand-500 text-white rounded-full flex-shrink-0 mr-3 text-center text-sm leading-6 font-semibold">2</span>
              <span>You can take your time to provide thoughtful answers</span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-6 h-6 bg-brand-500 text-white rounded-full flex-shrink-0 mr-3 text-center text-sm leading-6 font-semibold">3</span>
              <span>You can review and edit your responses before submitting</span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-6 h-6 bg-brand-500 text-white rounded-full flex-shrink-0 mr-3 text-center text-sm leading-6 font-semibold">4</span>
              <span>There is <strong>no time limit</strong> - complete at your own pace</span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-6 h-6 bg-brand-500 text-white rounded-full flex-shrink-0 mr-3 text-center text-sm leading-6 font-semibold">5</span>
              <span>Your responses will be saved as you type</span>
            </li>
          </ul>
        </div>

        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <div className="flex">
            <AlertCircle className="w-5 h-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-yellow-800">
                <strong>Important:</strong> Make sure you have a stable internet connection. 
                You can save your progress and return later before submitting.
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={handleStartQuestionnaire}
          className="w-full bg-brand-500 hover:bg-brand-600 text-white font-semibold py-4 px-6 rounded-lg transition duration-200 flex items-center justify-center"
        >
          <MessageSquare className="w-5 h-5 mr-2" />
          Start Questionnaire
        </button>

        <p className="text-center text-sm text-gray-500 mt-4">
          By starting the questionnaire, you agree to provide honest and thoughtful responses.
        </p>
      </div>
    </div>
  );
};

export default GeneralQuestionnaireWelcomeScreen;
